import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getSession } from 'next-auth/react';
import type { ConfigPublicEnv } from '@/@core/config';

type MutatorOptions = AxiosRequestConfig & {
    withAuth?: boolean;
    portalApiKey?: boolean;
};

function runtimeConfig<K extends keyof ConfigPublicEnv>(key: K): ConfigPublicEnv[K] {
    if (typeof window !== 'undefined') {
        return window.__REALICO_CONFIG__[key];
    }
    // Server-side SSR fallback — config validated at startup
    const fallbacks: Pick<ConfigPublicEnv, 'api.url' | 'api.key'> = {
        'api.url': process.env.API_URL ?? '',
        'api.key': process.env.API_PORTAL_KEY ?? '',
    };
    return (fallbacks as ConfigPublicEnv)[key];
}

export const mutator = async <T>(
    config: AxiosRequestConfig,
    options?: MutatorOptions,
): Promise<T> => {
    const baseUrl = runtimeConfig('api.url');
    const withAuth = options?.withAuth ?? false;
    let token: string | null = null;
    if (withAuth && typeof window !== 'undefined') {
        try {
            const session = await getSession();
            token = session?.user?.accessToken ?? null;
        } catch {
            token = null;
        }
    }

    const headers = {
        ...(config.headers ?? {}),
        ...(options?.headers ?? {}),
    } as Record<string, string>;

    if (
        !(config.data instanceof FormData) &&
        !(options?.data instanceof FormData) &&
        !headers['Content-Type']
    ) {
        headers['Content-Type'] = 'application/json';
    }

    if (options?.portalApiKey) {
        headers['x-portal-key'] = runtimeConfig('api.key');
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const { withAuth: _withAuth, ...requestOptions } = options ?? {};

    const response = await axios.request<T>({
        baseURL: baseUrl,
        ...config,
        ...requestOptions,
        headers,
        // validateStatus: (status) => status < 500,
        validateStatus: (status) => true,
    });

    if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:expired'));
    }

    return response.data;

    /*
    try {
        const response = await axios.request<T>({
            baseURL: baseUrl,
            ...config,
            ...requestOptions,
            headers,
        });

        return response.data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const status = err.response?.status;

            if (status === 401 && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('auth:expired'));
            }
        }

        throw err;
    }
    */
};
